import { apiClient } from './apiClient'
import { buildQueryString } from '../utils/pagination'
import type { Account } from '../types'

/** DD-1771 — GET /accounts/:id/two-factor-status */
export interface AccountTwoFactorStatus {
  required: boolean
  /** usuarios distintos asignados a la cuenta (account_user_profile) */
  users: number
  /** de esos, cuántos todavía no tienen 2FA configurado (DD-1875: sin contar los exentos) */
  usersWithout2FA: number
  /** DD-1875 — usuarios activos técnicos exceptuados del 2FA obligatorio (user.two_factor_exempt) */
  exemptUsers: number
}

export const accountService = {
  // Obtener todas las cuentas (sin filtros ni paginación)
  async getAllAccounts(): Promise<Account[]> {
    try {
      const response = await apiClient.get('/accounts/all')
      // El backend devuelve {success: true, data: Array}
      return response.data.data || response.data
    } catch (error) {
      console.error('Error fetching accounts:', error)
      throw error
    }
  },

  // Obtener cuentas con filtros y paginación
  async getAccounts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    operator_id?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<{
    data: Account[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const queryString = buildQueryString('/accounts', params || {})
      const url = queryString ? `/accounts?${queryString}` : '/accounts'
      const response = await apiClient.get(url)
      return {
        data: response.data.data || [],
        pagination: response.data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        }
      }
    } catch (error) {
      console.error('Error fetching accounts with filters:', error)
      throw error
    }
  },

  // DD-1771 — 2FA obligatorio por cuenta: estado + cuántos usuarios todavía no lo configuraron
  async getTwoFactorStatus(id: number): Promise<AccountTwoFactorStatus> {
    const response = await apiClient.get(`/accounts/${id}/two-factor-status`)
    return response.data.data
  },

  // DD-1771 — activar/desactivar el 2FA obligatorio (sólo superadmin; el server responde 403)
  async setTwoFactorRequired(id: number, required: boolean): Promise<{ required: boolean }> {
    const response = await apiClient.put(`/accounts/${id}/two-factor-required`, { required })
    return response.data.data
  },

  // Obtener cuenta por ID
  async getAccountById(id: number): Promise<Account> {
    try {
      const response = await apiClient.get(`/accounts/${id}`)
      // The backend returns { success: true, data: account }
      return response.data.data || response.data
    } catch (error) {
      console.error('Error fetching account:', error)
      throw error
    }
  },

  // Crear cuenta
  async createAccount(accountData: Partial<Account>): Promise<Account> {
    try {
      const response = await apiClient.post('/accounts', accountData)
      return response.data
    } catch (error) {
      console.error('Error creating account:', error)
      throw error
    }
  },

  // Actualizar cuenta
  async updateAccount(id: number, accountData: Partial<Account>): Promise<Account> {
    try {
      const response = await apiClient.put(`/accounts/${id}`, accountData)
      return response.data
    } catch (error) {
      console.error('Error updating account:', error)
      throw error
    }
  },

  // Eliminar cuenta
  async deleteAccount(id: number): Promise<void> {
    try {
      await apiClient.delete(`/accounts/${id}`)
    } catch (error) {
      console.error('Error deleting account:', error)
      throw error
    }
  },

  // Obtener servicios de subscription_services para una cuenta
  async getAccountServices(accountId: number): Promise<any[]> {
    try {
      const response = await apiClient.get(`/accounts/${accountId}/services`)
      return response.data.data || response.data || []
    } catch (error) {
      console.error('Error fetching account services:', error)
      throw error
    }
  },

  // Obtener tipos de cuenta
  async getAccountTypes(): Promise<any[]> {
    try {
      const response = await apiClient.get('/account-types')
      // The backend returns { success: true, data: [...] }
      return response.data.data || response.data
    } catch (error) {
      console.error('Error fetching account types:', error)
      throw error
    }
  },

  // Obtener variables de cuenta
  async getAccountVariables(accountId: number): Promise<any[]> {
    try {
      const response = await apiClient.get(`/accounts/${accountId}/variables`)
      return response.data.data || response.data
    } catch (error) {
      console.error('Error fetching account variables:', error)
      throw error
    }
  },

  // Obtener validadores MSISDN
  async getMsisdnValidators(): Promise<any[]> {
    try {
      const response = await apiClient.get('/msisdn-validators')
      // The backend returns { success: true, data: [...] }
      return response.data.data || response.data
    } catch (error) {
      console.error('Error fetching MSISDN validators:', error)
      throw error
    }
  },

  // Account Variables CRUD operations
  async createAccountVariable(data: any): Promise<any> {
    try {
      const response = await apiClient.post('/accounts/variables', data)
      return response.data
    } catch (error) {
      console.error('Error creating account variable:', error)
      throw error
    }
  },

  async updateAccountVariable(accountId: number, variableId: number, data: any): Promise<any> {
    try {
      const response = await apiClient.put(`/accounts/${accountId}/variables/${variableId}`, data)
      return response.data
    } catch (error) {
      console.error('Error updating account variable:', error)
      throw error
    }
  },

  async deleteAccountVariable(accountId: number, variableId: number): Promise<void> {
    try {
      await apiClient.delete(`/accounts/${accountId}/variables/${variableId}`)
    } catch (error) {
      console.error('Error deleting account variable:', error)
      throw error
    }
  }
}